import { useGSAP } from '@gsap/react';
import { PortableText } from '@portabletext/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useEffect, useRef, useState } from 'react';
import { useHasEnteredViewport } from '../../../utils/hooks';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { getComponents, getPortableTextTimeline } from '../../../portableText';
import { RawPortableText } from '../../../types/types';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './TextAndCarouselModule.module.scss';

const HERO_IMAGE_TRANSITION_PERIOD_MS = 3000;
const HERO_IMAGE_FADE_TIME_MS = 500;

export const TextAndCarouselModuleFragment = graphql`
  fragment TextAndCarouselModule on SanityTextAndCarouselModule {
    images {
      ...SanityImage
    }
    title {
      ...LocaleStringFragment
    }
    Text {
      ...LocaleListPortableText
    }
  }
`;

export type TextAndCarouselModuleProps = {
  title?: string;
  Text: RawPortableText;
  images: Array<SanityImageType>;
  className?: string;
};

function TextAndCarouselModule(
  props: TextAndCarouselModuleProps & CommonModuleProps,
): React.ReactElement {
  const { title, Text, images, className, moduleId } = props;

  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [nextImageIndex, setNextImageIndex] = useState<number | null>(null);
  const hasBeingInteracted = useRef<boolean>(false);

  useEffect(() => {
    if (!hasBeingInteracted.current) {
      const timeoutId = setTimeout(() => {
        if (!hasBeingInteracted.current) {
          setNextImageIndex((activeImageIndex + 1) % images.length);
        }
      }, HERO_IMAGE_TRANSITION_PERIOD_MS);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return;
  }, [activeImageIndex]);

  useEffect(() => {
    if (nextImageIndex !== null) {
      const timeoutId = setTimeout(() => {
        setActiveImageIndex(nextImageIndex);
        setNextImageIndex(null);
      }, HERO_IMAGE_FADE_TIME_MS);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return;
  }, [nextImageIndex]);

  const handleNextImage = () => {
    hasBeingInteracted.current = true;
    setNextImageIndex((activeImageIndex + 1) % images.length);
  };

  const handlePreviousImage = () => {
    hasBeingInteracted.current = true;
    setNextImageIndex((activeImageIndex - 1 + images.length) % images.length);
  };
  const containerRef = useRef<HTMLElement | null>(null);
  const [viewportRef, hasEnteredViewport] = useHasEnteredViewport();

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentWrapper}`,
          start: 'top 75%',
        } satisfies ScrollTrigger.Vars,
      });

      // portable text
      tl.add(getPortableTextTimeline(styles.text));

      // buttons (commented as we don't have buttons yet)
      // tl.from(`.${styles.buttonsContainer}`, {
      //   y: 30,
      //   autoAlpha: 0,
      //   duration: 0.5,
      // });
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      ref={containerRef}
      className={clsx(className, styles.container)}
      currentModuleBgColor={props.theme}
      contentClassName={styles.contentContainer}
      childrenClassName={styles.content}
    >
      {title && (
        <AnimatedTitle
          title={title}
          trigger={styles.contentContainer}
          resolution="mobile"
          className={styles.title}
        />
      )}
      <div className={styles.contentWrapper}>
        <div className={clsx(styles.mediaContainer)}>
          <div className={styles.carouselContainer} ref={viewportRef}>
            {images.map(
              (image, i) =>
                (activeImageIndex === i || nextImageIndex === i) && (
                  <div className={styles.imageContainer} key={i}>
                    <Image
                      image={image}
                      className={clsx(
                        styles.image,
                        activeImageIndex === i && styles.activeImage,
                        nextImageIndex === i && styles.nextImage,
                        activeImageIndex === i && nextImageIndex !== null && styles.fadeOut,
                      )}
                      width={750}
                      height={750}
                      cover
                      eagerLoading={hasEnteredViewport}
                    />
                    <button className={styles.leftArrow} onClick={handlePreviousImage}>
                      <div className={styles.visuallyHidden}>Previous image</div>
                      <FaChevronLeft />
                    </button>
                    <button className={styles.rightArrow} onClick={handleNextImage}>
                      <div className={styles.visuallyHidden}>Next image</div>
                      <FaChevronRight />
                    </button>
                  </div>
                ),
            )}
          </div>
          <div className={styles.carouselBarsContainer}>
            {images.map((image, i) => (
              <button
                className={clsx(
                  styles.carouselBarContainer,
                  (nextImageIndex !== null ? nextImageIndex : activeImageIndex) === i &&
                    styles.active,
                )}
                key={i}
                onClick={() => {
                  hasBeingInteracted.current = true;
                  if (nextImageIndex === null) {
                    if (activeImageIndex !== i) {
                      setNextImageIndex(i);
                    }
                  }
                }}
              >
                <div className={styles.visuallyHidden}>Navigate carousel</div>
              </button>
            ))}
          </div>
        </div>
        <div className={styles.textContainer}>
          {title && (
            <AnimatedTitle
              title={title}
              trigger={styles.contentContainer}
              resolution="desktop"
              className={styles.title}
            />
          )}
          <div className={styles.text}>
            <PortableText value={Text} components={getComponents()} />
          </div>
        </div>
      </div>
    </ModuleLayout>
  );
}

export default TextAndCarouselModule;
